import { HttpParams } from '@angular/common/http';
import { Configuration2D } from 'src/app/models/configurators-2d/configuration2d';
import { Quotation } from 'src/app/models/configurators-2d/quotation/quotation';
import { Resume } from 'src/app/models/configurators-2d/resume/resume';
import { CommonApp2dService } from './common-app-2d-service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { ShoppingCartProduct } from 'src/app/models/shopping-cart/shopping-cart-product';
import { OrderResponse } from 'src/app/models/configurators-2d/applications/carpentry/Elcia/order/order-response';
import { Order } from 'src/app/models/configurators-2d/order';
import { PdfViewModel } from 'src/app/models/pdf-view-model';
import { BaseTunnelFundingDto } from 'src/app/models/configurators-2d/tunnel/base-tunnel-funding-dto';
import { PricedItem } from 'src/app/models/configurators-2d/tunnel/priced-item';
import { Funding } from 'src/app/stores/tunnel/model/funding';
import { Price } from 'src/app/stores/tunnel/model/price';
import {
  LiteOfferResult,
  MlData,
  OfferResult,
} from 'src/app/models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { logError } from '../helpers/sentry.helper';
import { HbmPdfViewModel } from '../../models/configurators-2d/tunnel/common-hbm-tunnel-dtos';
import { OrderBeforeSendCheckResult } from 'src/app/models/configurators-2d/tunnel/order-before-send-check-result';

export abstract class BaseAppService<T extends Configuration2D> extends CommonApp2dService {
  private currentConfiguration: T = undefined;
  hasManualDiscount: boolean = false;

  constructor(
    protected applicationName: CONFIG_CONTEXT,
    protected configurationType: new () => T
  ) {
    super(applicationName);
  }

  /**
   * Fonction d'initialisation de la configuration
   */
  private newConfiguration<T>(TCreator: new () => T): T {
    return new TCreator();
  }

  get configuration(): T {
    if (this.currentConfiguration === undefined) {
      this.reset();
    }

    return this.currentConfiguration;
  }

  /**
   * Consentement déjà enregistré pour ce projet, si le projet actuellement chargé correspond.
   * Lit le champ interne (jamais le getter {@link configuration}, qui créerait un projet vide en effet de bord).
   */
  protected override async getPersistedContactedByPhone(configId: string): Promise<boolean | undefined> {
    return this.currentConfiguration?.id === configId ? this.currentConfiguration?.contactedByPhone : undefined;
  }

  /**
   * Met à jour la configuration chargée en mémoire avec le consentement résolu, pour qu'un
   * prochain envoi dans la même session (ex. "Liste d'achat" renvoyée sans rechargement) le
   * réutilise au lieu de rouvrir la pop-up.
   */
  protected override setPersistedContactedByPhone(configId: string, contactedByPhone: boolean): void {
    if (this.currentConfiguration?.id === configId) {
      this.currentConfiguration.contactedByPhone = contactedByPhone;
    }
  }

  /**
   * Lit le champ interne (jamais le getter {@link configuration}, qui créerait un projet vide en
   * effet de bord).
   */
  protected override get currentConfigurationIdForContactedByPhone(): string | undefined {
    return this.currentConfiguration?.id;
  }

  reset() {
    this.currentConfiguration = this.newConfiguration(this.configurationType);
  }

  /**
   * Simplifie les relations de la configuration
   * permet d'éviter les références cyclic
   */
  protected simplifyConfiguration(): T {
    return this.configuration;
  }

  override saveProject(): Promise<boolean> {
    if (this.configuration.id) {
      return this.updateProject();
    }
    return this.createProject();
  }

  private async createProject(): Promise<boolean> {
    try {
      this.currentConfiguration.id = await this.post<string>(
        `${this.apiUrl}Configuration`,
        this.simplifyConfiguration()
      );
      return true;
    } catch (err) {
      logError(err);
      return false;
    }
  }

  private async updateProject(): Promise<boolean> {
    try {
      this.currentConfiguration.id = await this.put<string>(
        `${this.apiUrl}Configuration`,
        this.simplifyConfiguration()
      );
      return true;
    } catch (err) {
      logError(err);
      return false;
    }
  }

  getThumbById(id: string): Promise<{ image: string }> {
    return this.get<{ image: string }>(`${this.apiUrl}Picture/Previews/${id}`);
  }

  /**
   * Fonction de récupération d'une configuration par son id
   * @param id id de la configuration
   * @param setConfig
   * @returns projet
   */
  async getProjectById(id: string, setConfig: boolean = false): Promise<T> {
    try {
      const params = new HttpParams().set('configurationId', id);
      const res = await this.get<T>(`${this.apiUrl}Configuration`, params);
      if (setConfig && res) {
        this.currentConfiguration = res;
      }
      return res;
    } catch (err) {
      logError(err);
      return undefined;
    }
  }

  /**
   * Création du devis pour la configuration (pas de création si devis déjà en cours)
   * @returns identifiant du devis
   */
  getQuotation(): Promise<Quotation> {
    const params = new HttpParams().set('id', this.configuration.id);
    return this.get<Quotation>(`${this.apiUrl}Order/Quotation`, params);
  }

  /**
   * Fonction de récupération d'un résumé à partir de la configuration en cours
   * @returns
   * @param config
   */
  async getResume<R extends Resume>(config?: any): Promise<R> {
    try {
      const res = await this.post(`${this.apiUrl}Configuration/Resume`, config ?? this.configuration);
      this.configuration.resume = res;
      this.hasManualDiscount = res.manualDiscount ?? false;
      return res as R;
    } catch (err) {
      logError(err);
      return undefined;
    }
  }

  /**
   * Génère le PDF dans la back
   * @returns Promise
   */
  generateNewPdf(dataPdf: PdfViewModel): Promise<any> {
    return this.post(this.baseApiUrl + 'Pdf/GeneratePdf', dataPdf);
  }

  async sendMailWithPdf(
    tunnelConfigurationId: string,
    dataPdf: HbmPdfViewModel,
    email: string,
    projectType: string
  ): Promise<any> {
    try {
      const params = new HttpParams().set('email', email).set('tunnelConfigurationId', tunnelConfigurationId);
      return await this.post(`${this.baseApiUrl}${projectType}/Data/SendMailPdf`, dataPdf, params);
    } catch (error) {
      logError("Erreur lors de l'envoi du mail PDF", error);
      throw error;
    }
  }

  /**
   * Hook commun utilisé avant l'envoi COM+ depuis le tunnel.
   * Le comportement par défaut n'ajoute aucun blocage ; chaque configurateur
   * peut le surcharger pour retourner une erreur métier et la modale associée.
   */
  async checkOrderBeforeSend(): Promise<OrderBeforeSendCheckResult> {
    return {
      isValid: true,
    };
  }

  setManualDiscount(manualDiscount: boolean): void {
    this.configuration.manualDiscount = manualDiscount;
  }

  /**
   * Fonction de préparation des commandes
   * @param product : liste des produits
   * @returns
   */
  async prepareOrder(product: ShoppingCartProduct): Promise<void> {
    return;
  }

  /**
   * Fonction d'envoi de la commande aux fournisseurs
   * @param products : liste des produits
   * @returns
   */
  createSupplierOrder(products: ShoppingCartProduct[]): Promise<OrderResponse> {
    return Promise.resolve(undefined);
  }

  /**
   * Recalcule le champs "hasDisabledProducts"
   */
  checkHasDisabledProducts(): void {
    this.currentConfiguration.hasDisabledProducts = false;
  }

  /**
   * Envoie de la commande découpage réduit.
   * @param orderId
   * @param opportunityNumber
   */
  public async updateOrderAndSaveProject(orderId: string, opportunityNumber: number): Promise<void> {
    try {
      await this.saveOpportunityNumber(orderId, opportunityNumber);
    } catch (error) {
      logError(error);
      throw new Error('La mise à jour de la commande a échouée, veuillez contacter un administrateur.');
    }

    try {
      await this.saveProject();
      this.configuration.orders = await this.getOrders(this.configuration.id);
    } catch (error) {
      logError(error);
      throw new Error('La sauvegarde du projet a échouée, veuillez contacter un administrateur.');
    }
  }

  /**
   * Récupère la dernière commande en cours.
   * @param orders
   * @returns
   */
  private getLastOrder(orders: Order[]): Order {
    if (orders.length === 0) {
      return undefined;
    }
    let order: Order;

    order = orders
      .toSorted(
        (previousOrder, currentOrder) =>
          new Date(previousOrder.sendDate).getTime() - new Date(currentOrder.sendDate).getTime()
      )
      .at(-1);

    return order;
  }

  /**
   * Vérifie si la configuration a ses valeurs de TunnelContext et TunnelId de rempli. Est appelé quand la config est chargée en mode tunnel.
   Permet de palier les configs créé avant la MEP du 12/03/2025 qui rajoutait le TunnelId et TunnelContext, pour afficher les projets inTunnel
   * @param id : id de la config
   * @param tunnelId : tunnelId à set dans le cas ou la valeur n'existe pas en base
   * @param tunnelContext : context à set dans le cas ou la valeur n'existe pas base
   * @returns
   */
  async checkForTunnelId(id: string, tunnelId: string, tunnelContext: CONFIG_CONTEXT) {
    try {
      let config = await this.getProjectById(id);
      if (!config) {
        logError(`No configuration found for id : ${id}`);
        return;
      }
      if (!config.tunnelId || !config.tunnelContext) {
        config.tunnelId = tunnelId;
        config.tunnelContext = tunnelContext;
        this.currentConfiguration = config;
        this.saveProject();
      }
    } catch (err) {
      logError(err);
      return;
    }
  }

  protected getHbm<TOutput>(url: string, params?: object): Promise<TOutput> {
    return this.get<TOutput>(url, this.buildHttpParamsFromObject(params));
  }

  protected postHbm<TInput, TOutput>(url: string, body?: TInput, params?: object): Promise<TOutput> {
    return this.post<TOutput>(url, body ?? {}, this.buildHttpParamsFromObject(params));
  }

  protected putHbm<TInput, TOutput>(url: string, body?: TInput, params?: object): Promise<TOutput> {
    return this.put<TOutput>(url, body ?? {}, this.buildHttpParamsFromObject(params));
  }

  protected deleteHbm<TInput, TOutput>(url: string, body?: TInput, params?: object): Promise<TOutput> {
    return this.delete<TOutput>(url, body, this.buildHttpParamsFromObject(params));
  }

  protected addPricedItem(pricedItems: (PricedItem | number)[]): PricedItem {
    let pricedItem: PricedItem = {
      fullPrice: 0,
      discountedPrice: 0,
    };

    for (const item of pricedItems) {
      const value = item ?? 0;
      if (typeof value === 'number') {
        pricedItem.fullPrice += value;
        pricedItem.discountedPrice += value;
      } else {
        pricedItem.fullPrice += value.fullPrice;
        pricedItem.discountedPrice += value.discountedPrice;
      }
    }

    return pricedItem;
  }

  protected toPrice(pricedItem: PricedItem | number): Price {
    if (pricedItem == null || pricedItem == undefined) return null;

    if (typeof pricedItem === 'number') {
      return new Price({
        baseAmount: pricedItem,
        actualAmount: pricedItem,
      });
    } else {
      return new Price({
        baseAmount: pricedItem.fullPrice,
        actualAmount: pricedItem.discountedPrice,
      });
    }
  }

  /**
   * Méthode permettant de sélectionner une offre dans le tableau offerTable depuis le code et la durée
   * @param selectedBareme
   * @param selectedDuration
   * @param offerTable
   * @private
   */
  protected getDataFromFundingOfferTable(
    selectedBareme: string,
    selectedDuration: number,
    offerTable: LiteOfferResult[]
  ): {
    mensuality: number;
    interest: number;
    vac: string;
    txtAssurance: string;
    total: number;
  } {
    let selectedOffer: LiteOfferResult = offerTable.find(
      x => x.code === selectedBareme && x.duration === selectedDuration
    );

    return {
      mensuality: selectedOffer?.mensuality,
      interest: selectedOffer?.interest,
      vac: selectedOffer?.vacText,
      txtAssurance: selectedOffer?.assuranceText,
      total: selectedOffer?.total,
    };
  }

  protected mapRawOffersLiteOfferResults(input: OfferResult[]): LiteOfferResult[] {
    return input.map(offer => this.mapOfferResultToLiteOfferResult(offer));
  }

  protected mapOfferResultToLiteOfferResult(offerResult: OfferResult): LiteOfferResult {
    if (!offerResult) {
      return {
        code: 'Aucune offre',
        codeType: null,
        offer: null,
        mensuality: null,
        duration: null,
        interest: null,
        total: null,
        taeg: null,
        freeOffer: null,
        assuranceText: null,
        vacText: null,
      };
    }
    return {
      code: offerResult.bareme,
      codeType: offerResult.bareme_type,
      offer: offerResult.offre_details?.libelle ?? '',
      mensuality: offerResult.mens_1,
      duration: offerResult.nbecheances,
      interest: offerResult.coutcredit,
      total: offerResult.totaldu,
      taeg: offerResult.taeg,
      freeOffer: offerResult.coutcredit === 0,
      assuranceText: offerResult.offre_details?.txt_assurance ?? '',
      vacText: offerResult.offre_details?.txt_exemple_vac ?? '',
    };
  }

  protected discountValueToDiscountEnum(value: number): DiscountTypeEnum {
    switch (value) {
      case 0:
        return DiscountTypeEnum.Discount0;
      case 5:
        return DiscountTypeEnum.Discount5;
      case 10:
        return DiscountTypeEnum.Discount10;
      case 20:
        return DiscountTypeEnum.Discount20;
    }
  }

  protected mapBaseTunnelFundingFunding(input: BaseTunnelFundingDto): Funding {
    if (input === undefined) {
      return this.buildEmptyFunding();
    }

    let fundingRawOffers: OfferResult[] = JSON.parse(input.offers);

    if (fundingRawOffers.length === 0) {
      return this.buildEmptyFunding();
    }

    let offer = this.selectBestOffer(fundingRawOffers);

    return {
      mensuality: offer.mensuality,
      duration: offer.duration,
      interest: offer.interest,
      offerTable: this.mapRawOffersLiteOfferResults(fundingRawOffers),
      mlData: JSON.parse(input.mlData) as MlData,
      txtAssurance: offer.assuranceText,
      vac: offer.vacText,
      code: offer.code,
    };
  }

  protected buildEmptyFunding(): Funding {
    return {
      mensuality: 0,
      duration: 0,
      interest: 0,
      total: 0,
      offerTable: [
        {
          code: 'Aucune offre',
          offer: '',
          mensuality: 0,
          duration: 0,
          interest: 0,
          total: 0,
          taeg: 0,
          freeOffer: true,
          codeType: '',
          assuranceText: '',
          vacText: '',
        },
      ],
      mlData: {
        ml_sofinco: '',
        ml_sanitaire: '',
      },
      txtAssurance: '',
      vac: '',
      code: 'Aucune offre',
    };
  }

  private selectBestOffer(offersResults: OfferResult[]): LiteOfferResult {
    return this.mapOfferResultToLiteOfferResult(offersResults.find(offer => offer.matching === 'offer_best'));
  }
}
